import React, { useEffect, useRef, useState } from 'react'
import { Typography, Form, Input, Button, Badge } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDots } from '@fortawesome/free-solid-svg-icons'
import { Message } from '@twilio/conversations/lib/message'
import { Participant } from '@twilio/conversations/lib/participant'
import { Link, RouteComponentProps, useHistory } from 'react-router-dom'
import BookingModal from '../../helpers/BookingModal/BookingModal'
import ProfileAvatar from '../../helpers/ProfileAvatar/ProfileAvatar'
import SingleMessage from './SingleMessage'
import SpinLoader from '../../helpers/SpinLoader'
import { useChatContext } from '../../../context/chat'
import { IUser, TChat, TExpert, TExpertProfile } from '../../../context/types'
import { useAuthContext } from '../../../context/auth'

import './messages.css'
import { Conversation } from '@twilio/conversations/lib/conversation'
import InfiniteScroll from 'react-infinite-scroll-component'

const { Title, Text } = Typography

type TParams = {
	id: string
}

type TPagination = {
	page: number
	pageSize: number
	presentTotal: number
	totalNo: number
	twilio: string
}

const defaultPagination: TPagination = { page: 0, pageSize: 10, presentTotal: 10, totalNo: 0, twilio: '' }

function Messages({
	match: {
		params: { id },
	},
}: RouteComponentProps<TParams>) {
	const { chat, actions } = useChatContext()
	const { auth } = useAuthContext()

	const [messages, setMessages] = useState<Message[]>([])
	const [currentChat, setCurrentChat] = useState<TChat>(null)
	const [participantLastReadMessageIndex, setParticipantLastReadMessageIndex] = useState<number>(0)
	const [messagesLoading, setMessagesLoading] = useState<boolean>(false)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [messageCount, setMessageCount] = useState<number | null>(null)
	const [paginationData, setPaginationData] = useState<TPagination>(defaultPagination)
	const [addedMsgChanges, setAddedMsgChanges] = useState({ index: null, addedMsg: null })

	const history = useHistory()
	const bottom = useRef<HTMLDivElement>()
	const [sendMessageForm] = Form.useForm()

	async function getLastReadMessageIndex(conversation: Conversation, newMsgAdded?: boolean) {
		if (newMsgAdded) {
			currentChat?.conversation?.updateLastReadMessageIndex(addedMsgChanges.addedMsg['index'])
			actions.updateMessageThread(id, auth.user.id)
			actions.getUnreadMessagesCount(addedMsgChanges.addedMsg)
		}
		const participants = await conversation?.getParticipants()
		const participant = participants.find((p: Participant) => p.identity !== auth.user.twilio_uid)
		setParticipantLastReadMessageIndex(participant?.lastReadMessageIndex)
		setAddedMsgChanges({ index: null, addedMsg: null })
	}

	useEffect(() => {
		if (messages?.length > 0) {
			setMessages([])
			setAddedMsgChanges({ index: null, addedMsg: null })
		}
		setPaginationData(defaultPagination)
		if (chat.chats?.length && !isNaN(Number(id))) {
			setCurrentChat(chat.chats.find((c: TChat) => c.id === Number(id)))
		} else {
			setCurrentChat(null)
		}
	}, [id, chat.chats.some((c) => !c.conversation)])

	useEffect(() => {
		if (currentChat) {
			setMessagesLoading(true)
			currentChat?.conversation?.getMessagesCount().then(async (totalCount) => {
				if (totalCount === messageCount) setMessageCount(null)
				if (messages?.length > 0) setMessages([])
				setPaginationData({
					page: totalCount - paginationData.pageSize > 0 ? totalCount - paginationData.pageSize : 0,
					pageSize: paginationData.pageSize > totalCount ? totalCount : paginationData.pageSize,
					presentTotal: paginationData.presentTotal > totalCount ? totalCount : paginationData.presentTotal,
					totalNo: totalCount,
					twilio: currentChat.conversation?.sid,
				})
				if (totalCount) {
					await currentChat?.conversation?.updateLastReadMessageIndex(totalCount - 1)
					actions.updateMessageThread(id, auth.user.id)
				}
				setMessageCount(totalCount)
			})
		}
	}, [currentChat])

	useEffect(() => {
		if (messageCount !== null && paginationData.twilio === currentChat?.conversation.sid) {
			setMessagesLoading(true)
			fetchMessages()

			const messageAdded = async (msg: Message) => {
				if (id !== undefined) {
					setAddedMsgChanges({ index: msg.index, addedMsg: msg })
				}
			}

			const participantUpdated = () => {
				getLastReadMessageIndex(currentChat?.conversation, false)
			}

			currentChat?.conversation?.on('messageAdded', messageAdded)
			currentChat?.conversation?.on('participantUpdated', participantUpdated)

			return () => {
				currentChat?.conversation?.off('messageAdded', messageAdded)
				currentChat?.conversation?.off('participantUpdated', participantUpdated)
			}
		}
	}, [messageCount])

	useEffect(() => {
		if (addedMsgChanges.index !== null && currentChat?.conversation) {
			getLastReadMessageIndex(currentChat?.conversation, true)
			setMessages((m) => [addedMsgChanges.addedMsg].concat(m))
			setPaginationData({
				page: paginationData.page,
				pageSize: paginationData.pageSize,
				presentTotal: paginationData.presentTotal + 1,
				totalNo: paginationData.totalNo + 1,
				twilio: currentChat?.conversation?.sid,
			})
		}
	}, [addedMsgChanges])

	useEffect(() => {
		if (messages.length > 0) bottom?.current?.scrollIntoView()
	}, [bottom, messages])

	const onPressEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (!(e.metaKey || e.ctrlKey)) return
		sendMessageForm.submit()
	}

	const getUser = (c: TChat): IUser => {
		if (c.toUser.id === auth.user.id) {
			return c.fromUser
		}
		return c.toUser
	}

	const getToParticipant = (c: TChat): IUser => {
		if (c.toUser.twilio_uid !== auth.user.twilio_uid) {
			return c.toUser
		}
		return c.fromUser
	}

	const getExpertProfile = (c: TChat): TExpertProfile => {
		if (!c?.toUser) return

		const user = c.toUser.id === auth.user.id ? c.fromUser : c.toUser

		return user.expertProfiles && user.expertProfiles[0]
	}

	const getExpert = (c: TChat): TExpert => {
		if (!c?.toUser) return

		const user = c.toUser.id === auth.user.id ? c.fromUser : c.toUser

		return user.expertProfiles && user.expertProfiles[0]
			? {
					...user.expertProfiles[0].pages[0],
					profile: { ...user.expertProfiles[0], user },
			  }
			: undefined
	}

	const handleChat = (chatId: number) => () => {
		if (messagesLoading) return
		history.push(`/messages/${chatId}`)
	}

	const onSendMessage = async ({ message }: { message: string }) => {
		if (!message) return

		sendMessageForm.resetFields()
		const toUser = getToParticipant(currentChat)
		actions.updateMessageThread(id, toUser.id)
		await currentChat?.conversation?.sendMessage(message)
	}

	const fetchMessages = () => {
		currentChat?.conversation
			?.getMessages(paginationData.pageSize, paginationData.page, 'forward')
			.then(async (msgs) => {
				msgs.items.reverse().map((msg: Message) => {
					messages.push(msg)
				})
				if (msgs.items.length) {
					actions.getUnreadMessagesCount(msgs.items[0])
				}
				getLastReadMessageIndex(currentChat.conversation, false)
				setMessages(messages)
				setPaginationData({
					page: paginationData.page - paginationData.pageSize > 0 ? paginationData.page - paginationData.pageSize : 0,
					pageSize:
						paginationData.totalNo - paginationData.presentTotal > paginationData.pageSize
							? paginationData.pageSize
							: paginationData.totalNo - paginationData.presentTotal,
					presentTotal:
						paginationData.presentTotal + paginationData.pageSize > paginationData.totalNo
							? paginationData.totalNo
							: paginationData.presentTotal + paginationData.pageSize,
					totalNo: paginationData.totalNo,
					twilio: currentChat.conversation?.sid,
				})
				setMessagesLoading(false)
			})
			.catch(() => {
				setMessagesLoading(false)
			})
	}

	const onChatScroll = () => {
		if (paginationData.pageSize && paginationData.totalNo > 0) {
			setMessagesLoading(true)
			fetchMessages()
		}
	}

	return (
		<div className="chat">
			<div className="chat-sidebar">
				<Title className="chat-sidebar-title" level={2}>
					My Chats
				</Title>
				<div className="chat-items-list">
					{chat.chats.map((chatItem: TChat) => (
						<div
							key={chatItem.id}
							className={`chat-item ${chatItem.id === currentChat?.id ? 'chat-item-selected' : ''}`}
							onClick={handleChat(chatItem.id)}
						>
							<Badge count={chatItem.unreadMessagesCount} size="small">
								<ProfileAvatar size={35} src={getUser(chatItem).profile_pic_url} />
							</Badge>
							<Text style={{ fontSize: 16, color: 'inherit' }} className="chat-item-name">
								{getUser(chatItem).first_name} {getUser(chatItem).last_name}
							</Text>
						</div>
					))}
				</div>
			</div>
			<SpinLoader loading={!!chat.loading || messagesLoading} className="chat-main">
				<>
					<div>
						{!!currentChat ? (
							<div className="chat-header-user">
								<ProfileAvatar src={getUser(currentChat).profile_pic_url} style={{ marginRight: 20 }} />
								{!getExpertProfile(currentChat) && (
									<Text className="chat-header-user-name">
										{getUser(currentChat).first_name} {getUser(currentChat).last_name}
									</Text>
								)}
								{!!getExpertProfile(currentChat) && (
									<div className="chat-header-user-expert">
										<Link to={'/expert/' + getExpertProfile(currentChat).pages[0].slug}>
											<Button type="link" className="chat-header-user-name">
												{getUser(currentChat).first_name} {getUser(currentChat).last_name}
											</Button>
										</Link>
										<Button type="primary" shape="round" onClick={() => setIsModalVisible(true)}>
											Book a call
										</Button>
									</div>
								)}
							</div>
						) : (
							<Text className="chat-choose-chat">Choose chat</Text>
						)}
					</div>
					<div className="chat-message-zone" id="scrollableDiv" ref={bottom}>
						<InfiniteScroll
							dataLength={paginationData.presentTotal}
							next={onChatScroll}
							style={{ display: 'flex', flexDirection: 'column-reverse' }} //To put endMessage and loader to the top.
							inverse={true}
							hasMore={true}
							loader={<></>}
							scrollableTarget="scrollableDiv"
						>
							{!!currentChat ? (
								<>
									{messages.map((msg: Message) => {
										const user = msg.author === auth.user.twilio_uid ? auth.user : getUser(currentChat)
										const isMessageRead = !(
											participantLastReadMessageIndex === null || participantLastReadMessageIndex < msg.index
										)
										return (
											<SingleMessage
												key={msg.sid}
												body={msg.body}
												time={msg.dateCreated}
												timezone={auth.user.timezone}
												avatarURL={user.profile_pic_url}
												fromCurrentUser={msg.author === auth.user.twilio_uid}
												messageReadStatus={isMessageRead}
											/>
										)
									})}
								</>
							) : (
								<FontAwesomeIcon
									className="chat-no-chat-icon"
									icon={faCommentDots}
									color="#F0F0F0"
									size="10x"
									fontWeight="100"
								/>
							)}
						</InfiniteScroll>
					</div>
					<div className={`chat-form-zone ${!currentChat ? 'chat-form-hidden' : ''}`}>
						<Form layout="inline" form={sendMessageForm} onFinish={onSendMessage}>
							<Form.Item name="message" className="chat-textarea-wrapper">
								<Input.TextArea className="chat-textarea" onPressEnter={onPressEnter} />
							</Form.Item>
							<Form.Item style={{ marginRight: 0 }}>
								<Button
									style={{ color: '#197FF2' }}
									size="large"
									type="text"
									htmlType="submit"
									icon={<SendOutlined />}
								/>
							</Form.Item>
						</Form>
					</div>
					{!!getExpert(currentChat) && (
						<BookingModal
							visible={isModalVisible}
							onCancel={() => setIsModalVisible(false)}
							expert={getExpert(currentChat)}
						/>
					)}
				</>
			</SpinLoader>
		</div>
	)
}

export default Messages
